const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

export interface Trunk {
  id: string;
  name: string;
  enabled: boolean;
  sipHost: string;
  sipPort: number;
  transport: 'udp' | 'tcp' | 'tls';
  outboundProxy: string | null;
  authMode: 'register' | 'ip';
  username: string | null;
  hasPassword: boolean;
  authRealm: string | null;
  fromDomain: string | null;
  callerIdInFrom: boolean;
  callerIdNumber: string | null;
  codecPrefs: string;
  maxChannels: number;
  dialPrefix: string;
  stripDigits: number;
  priority: number;
  registration: string;
}

export interface User {
  id: string;
  email: string;
  displayName: string;
  role: 'admin' | 'supervisor' | 'agent';
  extension: string;
  active: boolean;
  numbers: Array<{
    id: string;
    e164: string;
    isDefault: boolean;
    canPresent: boolean;
    canReceive: boolean;
  }>;
}

export interface NumberPermission {
  numberId: string;
  /** Outbound: may present this number as caller ID. */
  present: boolean;
  /** Inbound: calls to this number ring this user. */
  receive: boolean;
  isDefault: boolean;
}

export interface Voicemail {
  id: string;
  callId: string;
  fromNumber: string | null;
  contactName: string | null;
  durationSec: number | null;
  listenedAt: string | null;
  createdAt: string;
  ownerName: string | null;
  toNumber: string | null;
  toLabel: string | null;
  hasAudio: boolean;
  transcriptStatus: string | null;
  transcript: string | null;
}

export interface PhoneNumber {
  id: string;
  e164: string;
  label: string | null;
  trunkId: string | null;
  trunkName: string | null;
  destType: 'user' | 'ring_group' | 'voicemail' | 'hangup';
  destUserId: string | null;
  destUserName: string | null;
  destGroupId: string | null;
  destGroupName: string | null;
  greetingName: string | null;
  voicemailGreetingName: string | null;
  voicemailEnabled: boolean;
  ringTimeoutSec: number;
}

export interface Call {
  id: string;
  direction: string;
  fromNumber: string | null;
  toNumber: string | null;
  startedAt: string;
  durationSec: number | null;
  userName: string | null;
  recordingId: string | null;
  transcriptStatus: string | null;
  transcriptPreview: string | null;
}

/**
 * Access token in memory only.
 *
 * sessionStorage is readable by any script that gets injected, so an XSS bug
 * would hand over an admin session that can change trunk credentials. Holding
 * it in a closure means a page reload asks for a password again — the right
 * trade for the one account that can cost real money.
 */
let token: string | null = null;

export function isAuthenticated(): boolean {
  return token !== null;
}

export function clearToken(): void {
  token = null;
}

/** Stable per-browser id, so the server can bind and revoke this session. */
function adminDeviceId(): string {
  const KEY = 'admin-device-id';
  let id = localStorage.getItem(KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(KEY, id);
  }
  return id;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  // An expired token should drop us to the login screen, not show a confusing
  // permissions error on every panel.
  if (res.status === 401) {
    clearToken();
    window.location.reload();
    throw new Error('Session expired');
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: string;
      message?: string;
    };
    throw new Error(body.message ?? body.error ?? res.statusText);
  }

  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

/**
 * Fetches audio with the access token attached and returns a blob URL.
 *
 * Callers must revoke the URL when done, or a long session leaks a few MB per
 * message played.
 */
async function fetchAudio(path: string): Promise<string> {
  const res = await fetch(`${BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? res.statusText);
  }

  return URL.createObjectURL(await res.blob());
}

export async function login(email: string, password: string): Promise<void> {
  const result = await request<{ token: string; user: { role: string } }>(
    '/api/auth/login',
    {
      method: 'POST',
      body: JSON.stringify({
        email,
        password,
        deviceLabel: 'admin-panel',
        // Required by the server to bind the session to this browser.
        deviceId: adminDeviceId(),
      }),
    },
  );

  if (result.user.role !== 'admin') {
    throw new Error('This account does not have admin access');
  }

  token = result.token;
}

export const api = {
  trunks: () => request<Trunk[]>('/api/admin/trunks'),
  createTrunk: (body: unknown) =>
    request<{ id: string }>('/api/admin/trunks', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateTrunk: (id: string, body: unknown) =>
    request(`/api/admin/trunks/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteTrunk: (id: string) => request(`/api/admin/trunks/${id}`, { method: 'DELETE' }),
  syncTrunks: () => request('/api/admin/trunks/sync', { method: 'POST' }),

  users: () => request<User[]>('/api/admin/users'),
  createUser: (body: unknown) =>
    request<{ id: string; temporaryPassword?: string }>('/api/admin/users', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateUser: (id: string, body: unknown) =>
    request(`/api/admin/users/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  resetPassword: (id: string) =>
    request<{ temporaryPassword?: string }>(`/api/admin/users/${id}/password`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  numbers: () => request<PhoneNumber[]>('/api/admin/numbers'),
  createNumber: (body: unknown) =>
    request<{ id: string }>('/api/admin/numbers', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  deleteNumber: (id: string) => request(`/api/admin/numbers/${id}`, { method: 'DELETE' }),

  calls: (limit = 100) => request<{ calls: Call[] }>(`/api/calls?limit=${limit}`),
  recordingAudio: (callId: string) => fetchAudio(`/api/calls/${callId}/recording`),
  transcript: (callId: string) =>
    request<{
      transcript: { text: string; segments: unknown[] } | null;
      transcriptionProgress: import('./TranscriptionProgress.js').Progress | null;
    }>(`/api/calls/${callId}`),

  /** Queue health. `stalled` is the number worth alerting on. */
  transcriptionQueue: () =>
    request<{
      queued: number;
      processing: number;
      stalled: number;
      rate: number;
      estimatedDrainSeconds: number;
    }>('/api/transcription/queue'),

  voicemail: (limit = 100) =>
    request<{ voicemails: Voicemail[]; unread: number }>(`/api/voicemail?limit=${limit}`),
  /**
   * Audio arrives as decrypted bytes over an authenticated request, so it
   * cannot be dropped straight into an <audio src>: the element sends no
   * Authorization header. Fetch it here and hand back an object URL.
   */
  voicemailAudio: (id: string) => fetchAudio(`/api/voicemail/${id}/audio`),
  markVoicemailRead: (id: string, read: boolean) =>
    request(`/api/voicemail/${id}/read`, { method: 'POST', body: JSON.stringify({ read }) }),
  deleteVoicemail: (id: string) => request(`/api/voicemail/${id}`, { method: 'DELETE' }),

  /**
   * Greeting upload. Sent as multipart rather than JSON, so the Content-Type
   * header must be left for the browser to set with its own boundary.
   */
  uploadGreeting: async (numberId: string, slot: 'greeting' | 'voicemail', file: File) => {
    const form = new FormData();
    form.append('file', file);

    const res = await fetch(`${BASE}/api/admin/numbers/${numberId}/greeting?slot=${slot}`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });

    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
      throw new Error(body.message ?? body.error ?? res.statusText);
    }
    return res.json() as Promise<{ slot: string; name: string; bytes: number }>;
  },

  uploadHoldMusic: async (file: File) => {
    const form = new FormData();
    form.append('file', file);

    const res = await fetch(`${BASE}/api/admin/hold-music`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });

    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
      throw new Error(body.message ?? body.error ?? res.statusText);
    }
    return res.json() as Promise<{ name: string; bytes: number }>;
  },

  removeHoldMusic: () => request('/api/admin/hold-music', { method: 'DELETE' }),

  removeGreeting: (numberId: string, slot: 'greeting' | 'voicemail') =>
    request(`/api/admin/numbers/${numberId}/greeting?slot=${slot}`, { method: 'DELETE' }),

  settings: () => request<Record<string, unknown>>('/api/admin/settings'),
  updateSettings: (body: unknown) =>
    request('/api/admin/settings', { method: 'PUT', body: JSON.stringify(body) }),

  // --- Desktop app distribution ------------------------------------------

  installer: () => request<InstallerInfo>('/api/admin/installer'),

  /**
   * Upload a build. XHR rather than fetch so the ~90 MB transfer shows a
   * progress bar — fetch cannot report upload progress.
   */
  uploadInstaller: (
    file: File,
    version: string,
    notes: string,
    latest: File | null,
    onProgress: (fraction: number) => void,
  ): Promise<{ filename: string; version: string; size: number; sha256: string }> =>
    new Promise((resolve, reject) => {
      const form = new FormData();
      form.append('version', version);
      form.append('notes', notes);
      // Fields must precede the file so the server sees them first while it
      // streams the large part.
      form.append('file', file);
      if (latest) form.append('latest', latest);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${BASE}/api/admin/installer`);
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(e.loaded / e.total);
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve(JSON.parse(xhr.responseText));
        else {
          const msg = (() => {
            try {
              return JSON.parse(xhr.responseText).message ?? JSON.parse(xhr.responseText).error;
            } catch {
              return xhr.statusText;
            }
          })();
          reject(new Error(msg || 'Upload failed'));
        }
      };
      xhr.onerror = () => reject(new Error('Upload failed'));
      xhr.send(form);
    }),

  /** Let the browser stream the installer using an authenticated, single-use link. */
  downloadInstaller: async (): Promise<void> => {
    const { link } = await request<{ link: string }>('/api/admin/installer/link', {
      method: 'POST',
      body: JSON.stringify({ maxUses: 1 }),
    });
    const a = document.createElement('a');
    a.href = link;
    a.download = '';
    a.hidden = true;
    document.body.appendChild(a);
    a.click();
    a.remove();
  },

  inviteUser: (id: string, maxUses: number | null) =>
    request<InviteResult>(`/api/admin/users/${id}/invite`, {
      method: 'POST',
      body: JSON.stringify({ maxUses }),
    }),

  createInstallerLink: (maxUses: number | null) =>
    request<{ link: string; expiresAt: string }>('/api/admin/installer/link', {
      method: 'POST',
      body: JSON.stringify({ maxUses }),
    }),
};

export interface InstallerInfo {
  installed: boolean;
  filename?: string;
  version?: string;
  size?: number;
  sha256?: string;
  uploadedAt?: string;
  notes?: string | null;
}

export interface InviteResult {
  link: string;
  expiresAt: string;
  emailed: boolean;
  emailError?: string;
  mailto?: string;
}
